import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import productService from "../../services/productService";
import { KamaModal } from "../structure/KamaModal";
import { formatKatalogKodu } from "../../utils/formatters";
import { 
    IconBinary, 
    IconInfoCircle, 
    IconLayoutGrid, 
    IconTag, 
    IconSearch,
    IconLoader,
    IconFileDescription
} from "@tabler/icons-react";

export const CodeResolver = () => {
    const { t } = useTranslation();
    const [code, setCode] = useState("");
    const [loading, setLoading] = useState(false);
    const [parsedData, setParsedData] = useState(null);
    const [showModal, setShowModal] = useState(false);

    const handleResolve = async (e) => {
        if (e) e.preventDefault();

        let cleanCode = code.trim().toUpperCase();
        // Remove trailing description keywords
        cleanCode = cleanCode.replace(/\s+(RULET|FRENLI|TEKSTIL|GRI|OYNAK|SABIT|YAYLI|FRENLİ)$/i, "");
        // Remove all whitespaces
        cleanCode = cleanCode.replace(/\s+/g, "");

        if (!cleanCode) {
            toast.warning(t('mesajKodBosOlamaz', 'Lütfen bir ürün kodu giriniz.'));
            return;
        }

        setLoading(true);
        try {
            const result = await productService.parseCode(cleanCode);
            if (result) {
                setParsedData(result);
                setShowModal(true);
            } else {
                toast.error(t('mesajKodCozumlenemedi', 'Kod çözümlenemedi. Lütfen geçerli bir kod girin.'));
            }
        } catch (error) {
            console.error("Resolve error", error);
            if (error.response?.status === 404) {
                toast.error(t('mesajKodBulunamadi', 'Belirttiğiniz Teker Kodu Kama Rulet Kodlama Sistemine uygun olmadığı için çözülememiştir'));
            } else {
                toast.error(t('mesajSistemHatasi', 'İşlem sırasında bir hata oluştu.'));
            }
        } finally {
            setLoading(false);
        }
    };

    const details = parsedData?.Detaylar;
    const components = parsedData?.Bilesenler;

    // Filter out null or empty parsed fields for the details table
    const detailRows = details ? [
        { label: t("DetayMarka", "Marka"), val: details.Marka, code: components?.Marka },
        { label: t("DetayOnKod", "Ön Kod"), val: details.OnKod, code: components?.OnKod },
        { label: t("DetaySeri", "Seri"), val: details.Seri, code: components?.Seri },
        { label: t("DetayBaglanti", "Bağlantı Tipi"), val: details.Baglanti, code: components?.Baglanti },
        { label: t("DetayGovde", "Gövde Cinsi"), val: details.Govde, code: components?.Govde },
        { label: t("DetaySirtTipi", "Sırt Tipi"), val: details.SirtTipi, code: components?.SirtTipi },
        { label: t("DetaySirtMalzeme", "Sırt Malzemesi"), val: details.SirtMalzemesi, code: components?.SirtMalzemesi },
        { label: t("DetayYatak", "Yatak Tipi"), val: details.Yatak, code: components?.Yatak },
        { label: t("DetayCap", "Tekerlek Çapı"), val: details.Cap, code: components?.Cap },
        { label: t("DetayGenislik", "Tekerlek Genişliği"), val: details.Genislik, code: components?.Genislik },
        { label: t("DetayFren", "Fren Tipi"), val: details.Fren, code: components?.Fren },
        { label: t("DetayYay", "Yay Kodu"), val: details.Yay, code: components?.Yay },
        { label: t("DetayRenk", "Renk"), val: details.Renk, code: components?.Renk },
    ].filter(row => row.val) : [];

    return (
        <div className="page py-5">
            <div className="container" style={{ maxWidth: "700px" }}>
                
                {/* Visual Header */}
                <div className="text-center mb-5">
                    <div 
                        className="d-inline-flex align-items-center justify-content-center mb-3"
                        style={{
                            width: "72px",
                            height: "72px",
                            borderRadius: "20px",
                            background: "linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)",
                            border: "1px solid #bfdbfe",
                            color: "#2563eb"
                        }}
                    >
                        <IconBinary size={36} />
                    </div>
                    <h1 className="h3 fw-bold text-slate-900 mb-2" style={{ color: "#0f172a" }}>
                        {t("menuTekerKoduCozumleyici", "Teker Kodu Çözümleyici")}
                    </h1>
                    <p className="text-secondary" style={{ fontSize: "15px" }}>
                        {t("TekerKoduAciklama", "Kama katalog veya orijinal ürün kodunu girerek tüm teknik bileşenlerini anında çözümleyin.")}
                    </p>
                </div>

                {/* Form Card */}
                <div className="card border-0 shadow-lg rounded-4 overflow-hidden">
                    <div className="card-body p-4 p-md-5">
                        <form onSubmit={handleResolve}>
                            <div className="mb-4">
                                <label 
                                    htmlFor="productCodeInput" 
                                    className="form-label fw-semibold mb-3 text-secondary text-uppercase"
                                    style={{ fontSize: "12px", letterSpacing: "1px" }}
                                >
                                    {t("KatalogVeyaOrjinalKodu", "Katalog Kodu")}
                                </label>
                                <input 
                                    type="text" 
                                    id="productCodeInput"
                                    className="form-control form-control-lg text-center font-monospace shadow-sm"
                                    style={{
                                        fontSize: "2rem",
                                        fontWeight: "bold",
                                        letterSpacing: "1px",
                                        borderRadius: "12px",
                                        textTransform: "uppercase",
                                        borderColor: "#cbd5e1",
                                        height: "70px",
                                        color: "#1e293b"
                                    }}
                                    placeholder="Örn: 3300PUB125OT"
                                    value={code}
                                    onChange={(e) => setCode(e.target.value.toUpperCase())}
                                    disabled={loading}
                                    autoFocus
                                />
                            </div>

                            <button 
                                type="submit" 
                                className="btn btn-primary btn-lg w-100 py-3 fw-bold d-flex align-items-center justify-content-center gap-2 shadow"
                                style={{
                                    borderRadius: "12px",
                                    backgroundColor: "#2563eb",
                                    borderColor: "#2563eb",
                                    fontSize: "16px",
                                    transition: "all 0.15s ease-in-out"
                                }}
                                onMouseEnter={e => {
                                    e.currentTarget.style.backgroundColor = "#1d4ed8";
                                    e.currentTarget.style.borderColor = "#1d4ed8";
                                }}
                                onMouseLeave={e => {
                                    e.currentTarget.style.backgroundColor = "#2563eb";
                                    e.currentTarget.style.borderColor = "#2563eb";
                                }}
                                disabled={loading}
                            >
                                {loading ? (
                                    <>
                                        <IconLoader className="spin" size={20} />
                                        <span>{t("Cozumleniyor", "Çözümleniyor...")}</span>
                                    </>
                                ) : (
                                    <>
                                        <IconSearch size={20} />
                                        <span>{t("Cozumle", "Çözümle")}</span>
                                    </>
                                )}
                            </button>
                        </form>
                    </div>
                </div>

            </div>

            {/* Product Specs Modal (Popup) */}
            <KamaModal
                show={showModal}
                onClose={() => setShowModal(false)}
                title={t('KamaRuletKodAciklamasi', 'Kama Rulet Kod Açıklaması')}
                icon={<IconLayoutGrid size={20} />}
                headerGradient="linear-gradient(135deg, #1e2a38 0%, #2563eb 100%)"
                size="lg"
            >
                <div className="px-2 py-1">
                    {/* Combined Summary Card (Ürün Kodu & Ürün Açıklaması) */}
                    {parsedData && (
                        <div className="card border-0 rounded-4 shadow-sm mb-4" style={{ backgroundColor: "#f8fafc", border: "1px solid #e2e8f0" }}>
                            <div className="card-body p-4">
                                <div className="row g-4 align-items-start">
                                    {/* Sol Kolon: Ürün Kodu */}
                                    <div className="col-12 col-md-5 d-flex flex-column align-items-start pe-md-4" style={{ borderRight: "1px solid #e2e8f0" }}>
                                        <div className="d-flex align-items-center gap-1.5 mb-2" style={{ color: '#94a3b8', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px' }}>
                                            <IconTag size={14} />
                                            {t("KatalogKoduBaslik", "KATALOG KODU")}
                                        </div>
                                        <span className="badge" style={{ backgroundColor: "#1e293b", color: "#ffffff", fontSize: "18px", fontWeight: 700, padding: "7px 14px", borderRadius: "8px", letterSpacing: "0.2px" }}>
                                            {formatKatalogKodu(parsedData.KatalogKodu)}
                                        </span>
                                    </div>

                                    {/* Sağ Kolon: Ürün Açıklaması */}
                                    <div className="col-12 col-md-7 ps-md-4">
                                        <div className="d-flex align-items-center gap-1.5 mb-2" style={{ color: '#94a3b8', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px' }}>
                                            <IconFileDescription size={14} />
                                            {t("TamUrunAciklamasi", "Ürün Açıklaması")}
                                        </div>
                                        <p className="m-0 text-slate-700 fw-medium" style={{ color: "#334155", fontSize: "14px", lineHeight: "1.6" }}>
                                            {parsedData.TamAciklama || t("DetayBulunamadi", "Açıklama bulunamadı.")}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Özellikler Tablosu */}
                    {detailRows.length > 0 ? (
                        <div className="table-responsive rounded-4 border overflow-hidden shadow-sm mb-4" style={{ borderColor: "#e2e8f0" }}>
                            <table className="table table-striped table-hover mb-0 align-middle">
                                <thead>
                                    <tr style={{ background: "#f8fafc" }}>
                                        <th className="ps-4 py-3 text-secondary font-semibold" style={{ width: "35%", fontSize: "11px", textTransform: "uppercase" }}>{t("Ozellik", "Özellik")}</th>
                                        <th className="py-3 text-secondary font-semibold text-center" style={{ width: "20%", fontSize: "11px", textTransform: "uppercase" }}>{t("KodKarakteri", "Kod Karakteri")}</th>
                                        <th className="pe-4 py-3 text-secondary font-semibold" style={{ fontSize: "11px", textTransform: "uppercase" }}>{t("Aciklama", "Açıklama")}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {detailRows.map((row, idx) => (
                                        <tr key={idx}>
                                            <td className="ps-4 py-2.5 fw-semibold text-slate-700" style={{ color: "#475569", fontSize: "13px" }}>
                                                {row.label}
                                            </td>
                                            <td className="py-2.5 text-center">
                                                <span className="badge px-2 py-0.5 rounded" style={{ backgroundColor: "#f1f5f9", color: "#0f172a", fontSize: "12px", fontWeight: "bold", border: "1px solid #cbd5e1" }}>
                                                    {row.code || "-"}
                                                </span>
                                            </td>
                                            <td className="pe-4 py-2.5 text-slate-900 fw-medium" style={{ color: "#0f172a", fontSize: "13px" }}>
                                                {row.val}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="p-4 text-center text-muted rounded-4 bg-light mb-4">
                            <IconInfoCircle size={24} className="mb-2" />
                            <p className="m-0">{t("DetayBulunamadi", "Ürün özellik detay bilgisi bulunamadı.")}</p>
                        </div>
                    )}

                    {/* Additional Information (badges) */}
                    {details?.Ilave && details.Ilave.length > 0 && (
                        <div className="p-4 rounded-4 bg-light border mb-4" style={{ borderColor: "#e2e8f0" }}>
                            <h6 className="fw-bold text-slate-700 mb-3 d-flex align-items-center gap-2" style={{ color: "#334155" }}>
                                <IconTag size={18} />
                                {t("IlaveBilgiler", "İlave Bilgiler ve Ek Özellikler")}
                            </h6>
                            <div className="d-flex flex-wrap gap-2">
                                {details.Ilave.map((item, idx) => (
                                    <span key={idx} className="badge bg-primary text-white rounded-pill px-3 py-2 fw-semibold" style={{ fontSize: "12px", letterSpacing: "0.2px" }}>
                                        {item}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Modal Footer Row */}
                    <div className="d-flex justify-content-end align-items-center pt-3 pb-2 mt-2">
                        <button
                            type="button"
                            className="btn btn-secondary px-4 py-2 fw-semibold rounded-3 shadow-sm"
                            onClick={() => setShowModal(false)}
                        >
                            {t("Kapat", "Kapat")}
                        </button>
                    </div>
                </div>
            </KamaModal>
        </div>
    );
};
